import type { Rng } from '../../../core/rng';
import type { GableType, Lot } from '../types';
import { lotKey } from '../lotKey';
import { sampleHouseParams } from '../houseStyle';
import type { BlockKeys } from '../painted/blockSides';
import committed from './woodcutReadings.json';

/** One block side as the woodcut draws it: its house fronts and, where their gables were counted, the stepped ones. */
export interface WoodcutSide {
  /** The side's house frontage in metres when it was read, so a re-planned side shows as another. */
  readonly frontage: number;
  readonly fronts: number;
  readonly stepped: number | null;
  readonly note: string;
}

/** The readings of the woodcut per block side (docs/plans/houses.md, section 8.4), and why the others were not read. */
export interface WoodcutReadings {
  readonly method: string;
  readonly scan: string;
  readonly sides: Readonly<Record<string, WoodcutSide>>;
  readonly unread: Readonly<Record<string, string>>;
}

/* A share read from fewer gables has a standard error above 0.2, so the district's shares say as much. */
export const MIN_READ_FRONTS = 5;

function fail(message: string): never {
  throw new Error(`woodcutReadings.json: ${message}`);
}

function isCount(v: unknown): v is number {
  return Number.isInteger(v) && (v as number) >= 0;
}

function parseSide(key: string, value: unknown): WoodcutSide {
  const s = value as Partial<WoodcutSide> | null;
  const ok =
    s !== null &&
    typeof s === 'object' &&
    typeof s.frontage === 'number' &&
    s.frontage > 0 &&
    isCount(s.fronts) &&
    (s.stepped === null || (isCount(s.stepped) && s.stepped <= s.fronts)) &&
    typeof s.note === 'string';
  if (!ok)
    fail(`the reading of ${key} is not a frontage, a front count and a stepped count within it`);
  return { frontage: s.frontage, fronts: s.fronts, stepped: s.stepped, note: s.note };
}

function parseMap<T>(
  name: string,
  value: unknown,
  parse: (key: string, v: unknown) => T
): Record<string, T> {
  if (value === null || typeof value !== 'object') fail(`${name} is not a map`);
  return Object.fromEntries(Object.entries(value).map(([key, v]) => [key, parse(key, v)]));
}

function parseReason(key: string, value: unknown): string {
  if (typeof value !== 'string') fail(`unread gives ${key} no reason`);
  return value;
}

/** The readings file, checked: read by eye on the woodcut's crops, and bundled by the build. */
export function parseWoodcutReadings(value: unknown): WoodcutReadings {
  const f = value as Partial<WoodcutReadings> | null;
  if (f === null || typeof f !== 'object' || typeof f.method !== 'string') fail('no method');
  if (typeof f.scan !== 'string') fail('no scan');
  return {
    method: f.method,
    scan: f.scan,
    sides: parseMap('sides', f.sides, parseSide),
    unread: parseMap('unread', f.unread, parseReason),
  };
}

export const WOODCUT_READINGS: WoodcutReadings = parseWoodcutReadings(committed);

function stepShare(side: WoodcutSide | undefined): number | null {
  if (!side || side.stepped === null || side.fronts < MIN_READ_FRONTS) return null;
  return side.stepped / side.fronts;
}

/*
 * The woodcut shows which gables are stepped, not which fronts are timber, so the stepped gables go
 * first to the houses whose own stream drew one, then to other brick fronts, then to timber ones:
 * the fewest fronts change from what their district's shares drew.
 */
function drawnClass(lot: Lot, rng: Rng): number {
  const drawn = sampleHouseParams(lot, rng.fork(lotKey(lot)));
  if (drawn.frontGable === 'stepped') return 0;
  return drawn.frontType === 'brick' ? 1 : 2;
}

function steppingOrder(lots: readonly Lot[], side: readonly number[], rng: Rng): number[] {
  const drawn = new Map(side.map((i) => [i, drawnClass(lots[i]!, rng)]));
  const rank = new Map(
    side.map((i) => [i, rng.fork(lotKey(lots[i]!)).fork('woodcutGable').next()])
  );
  return [...side].sort((a, b) => drawn.get(a)! - drawn.get(b)! || rank.get(a)! - rank.get(b)!);
}

/**
 * Each house's front gable where the woodcut's gables were counted on its block side: stepped on
 * the side's read share of its houses, pointed on the rest. Null for back houses and where the
 * side was not read, which draw from their district's shares.
 */
export function woodcutGables(
  lots: readonly Lot[],
  keys: readonly BlockKeys[],
  rng: Rng,
  file: WoodcutReadings = WOODCUT_READINGS
): (GableType | null)[] {
  const members = new Map<string, number[]>();
  lots.forEach((lot, i) => {
    const block = keys[i]!.block;
    if (lot.kind !== 'house' || stepShare(file.sides[block]) === null) return;
    members.set(block, [...(members.get(block) ?? []), i]);
  });
  const gables = new Map<number, GableType>();
  for (const [block, side] of members) {
    const order = steppingOrder(lots, side, rng);
    const stepped = Math.round(stepShare(file.sides[block])! * order.length);
    order.forEach((i, n) => gables.set(i, n < stepped ? 'stepped' : 'plain'));
  }
  return lots.map((_, i) => gables.get(i) ?? null);
}
